import {useAuth} from '../../../contexts/UserContext';
import {Navigate} from 'react-router-dom';
import {
  useRegistration,
  RegistrationContextProvider,
} from '../../../contexts/RegistrationContext';

import styled from 'styled-components';
import config from '../../../config';
import {SectionTitle} from '../../atoms/CommonParts';
import RegistrationPageStep0 from './step0';
import RegistrationPageStep1 from './step1';
import RegistrationPageStep2 from './step2';
import RegistrationPageStep3 from './step3';
import {useApp} from '../../../contexts/AppContext';

export const PageTitle = styled.div`
  height: 124px;
  display: flex;
  align-items: center;
  @media ${config.layout.wide} {
    height: 134px;
  }

  h1 {
    font-size 26px;
    font-weight: bold;
    line-height: 1;
    text-align: center;
    width: 100%;
  }
`;

const ScannedDeviceArea = styled.div`
  margin-bottom: 40px;
`;
const ScannedDeviceBlock = styled.div`
  margin-top: 20px;
  border: 1px solid #000000;
  border-radius: 2px;
  padding: 20px 20px 0;
  @media ${config.layout.wide} {
    padding: 20px;
    display: flex;
    align-items: center;
  }
`;
const ScannedDeviceImageArea = styled.div`
  height: 130px;
  background-color: #000;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  @media ${config.layout.wide} {
    width: calc(50% - 30px);
  }
`;
const ScannedDeviceInfoArea = styled.div`
  height: 130px;
  display: flex;
  //align-items: center;
  flex-direction: column;
  align-content: center;
  justify-content: center;
  @media ${config.layout.wide} {
    height: 130px;
    width: 50%;
    margin-left: 30px;
  }
`;

const ScannedDeviceInfo = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #000000;
  height: 38px;

  &:last-child {
    border-bottom: none;
  }

  h4 {
    font-size: 14px;
    //line-height: 19px;
    font-weight: bold;
    width: 130px;
    flex: 0 0 auto;
    padding-left: 11px;
  }

  span {
    font-size: 12px;
    //line-height: 26px;
    font-weight: 500;
    flex: 1 0 auto;
    white-space: nowrap;
  }
`;

const StepLabelArea = styled.div`
  margin: 30px 0 30px;
  height: 80px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &::before {
    content: "";
    display: block;
    position: absolute;
    z-index: 0;
    width: 100%;
    height: 1px;
    background-color: #000000;
    left: 0;
    top: 50%;
  }

  @media ${config.layout.wide} {
    width: 350px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const StepLabelItem = styled.div`
  position: relative;
  z-index: 1;
  width: 80px;
  background-color: #ffffff;
  height: 80px;
  text-align: center;
  border-radius: 50%;
  border: 1px solid #000000;
  font-size: 26px;
  font-weight: bold;
  padding-top: 14px;
  line-height: 23px;

  &.active {
    background-color: #000000;
    color: #fff;
  }

  &::before {
    content: "STEP";
    display: block;
    font-size: 12px;
  }
`;

const StepLabelSectionTitle = styled.div`
  span {
    position: relative;
    z-index: 1;
    font-size: 30px;
    font-weight: bold;
  }
  padding-left: 6px;
`;

const StepLabelSectionSubTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  line-height: 21px;
  padding-left: 6px;
  margin: 10px 0 20px 0;
  padding-bottom: 20px;
  border-bottom: 6px solid #000000;
`;

export function ScannedDevice() {
  const registration = useRegistration();

  if (!registration.state.scanned_device) {
    return '';
  }

  const device = registration.state.scanned_device;

  return (
      <ScannedDeviceArea>
        <SectionTitle>Scanned device</SectionTitle>
        <ScannedDeviceBlock>
          <ScannedDeviceImageArea
              style={{
                backgroundImage: `url(${device.image_url})`,
              }}
          />
          <ScannedDeviceInfoArea>
            <ScannedDeviceInfo>
              <h4>Device model</h4>
              <span>{device.device_name}</span>
            </ScannedDeviceInfo>
            <ScannedDeviceInfo>
              <h4>KUVO Device ID</h4>
              <span>{device.customer_id}</span>
            </ScannedDeviceInfo>
            <ScannedDeviceInfo>
              <h4>Serial no.</h4>
              <span>{device.serial_code || '- - - - - - - - - -'}</span>
            </ScannedDeviceInfo>
          </ScannedDeviceInfoArea>
        </ScannedDeviceBlock>
      </ScannedDeviceArea>
  );
}

export function StepLabel({children}) {
  const {state} = useRegistration();

  return (
      <>
        <StepLabelArea>
          <StepLabelItem className={`${state.step == 1 ? 'active' : ''}`}>
            <span>1</span>
          </StepLabelItem>
          <StepLabelItem className={`${state.step == 2 ? 'active' : ''}`}>
            <span>2</span>
          </StepLabelItem>
          <StepLabelItem className={`${state.step == 3 ? 'active' : ''}`}>
            <span>3</span>
          </StepLabelItem>
        </StepLabelArea>
      </>
  );
}

const StepWrapElem = styled.div`
  border: 1px solid #000000;
  border-radius: 2px;
  padding: 30px 20px 40px;
`;

export function StepWrap({
  subTitle,
  children,
}) {
  const {state} = useRegistration();

  return (
      <>
        <StepLabel/>
        <StepWrapElem>
          <StepLabelSectionTitle>
            <span>STEP{state.step}</span>
          </StepLabelSectionTitle>
          <StepLabelSectionSubTitle>{subTitle}</StepLabelSectionSubTitle>

          {children}
        </StepWrapElem>
      </>
  );
}

function RegistrationPageCore() {
  const registration = useRegistration();

  if (registration.state.step === 0) {
    return <RegistrationPageStep0/>;
  } else if (registration.state.step === 1) {
    return <RegistrationPageStep1/>;
  } else if (registration.state.step === 2) {
    return <RegistrationPageStep2/>;
  } else if (registration.state.step === 3) {
    return <RegistrationPageStep3/>;
  }

  return <div>RegistrationPageCore!</div>;
}

export default function RegistrationPage() {
  const auth = useAuth();

  if (auth.isLoggedIn()) {
    return (
        <Navigate to={`/devices/${window.location.search}#new`} replace={true}/>
    );
  }

  return (
      <RegistrationContextProvider>
        <RegistrationPageCore/>
      </RegistrationContextProvider>
  );
}
